import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Headphones, Loader2, Pause } from "lucide-react";
import { MalayalamLayout } from "../components/malayalam/MalayalamLayout";
import { useMalayalamLang } from "../context/MalayalamLangContext";
import { useAudio } from "../context/AudioContext";
import { Discussion } from "../components/Discussion";
import { C } from "../components/malayalam/mlShared";
import { mlBySlug, fileUrl, translateFields } from "../lib/api";

// Strip HTML tags to plain text for TTS.
const toPlainText = (html) => {
  const el = document.createElement("div");
  el.innerHTML = html || "";
  return (el.textContent || el.innerText || "").replace(/\s+/g, " ").trim();
};

export default function MalayalamArticle() {
  const { section, slug } = useParams();
  const { lang, t } = useMalayalamLang();
  const audio = useAudio();
  const [doc, setDoc] = useState(null);
  const [status, setStatus] = useState("loading");
  const [mlTrans, setMlTrans] = useState(null);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    setMlTrans(null);
    mlBySlug(section, slug)
      .then((d) => { if (alive) { setDoc(d); setStatus("ok"); } })
      .catch(() => { if (alive) setStatus("missing"); });
    return () => { alive = false; };
  }, [section, slug]);

  // Translate DB content on the fly when the reader switches to Malayalam and
  // the record has no stored *_ml fields (keeps the "translate body text" promise).
  useEffect(() => {
    if (lang !== "ml" || !doc || mlTrans || translating) return;
    const hasNative = (doc.title_ml || "").trim() && (doc.body_ml || "").trim();
    if (hasNative) return;
    setTranslating(true);
    translateFields(
      { title: doc.title_en || doc.title_ml || "", summary: doc.summary_en || "", body: doc.body_en || "" },
      "Malayalam", "ml"
    )
      .then((res) => setMlTrans(res || null))
      .catch(() => {})
      .finally(() => setTranslating(false));
  }, [lang, doc, mlTrans, translating]);

  const pick = (k) => {
    if (!doc) return "";
    if (lang === "ml") {
      const native = doc[`${k}_ml`];
      if (native && native.trim()) return native;
      if (mlTrans && (mlTrans[k] || "").trim()) return mlTrans[k];
      return doc[`${k}_en`] || "";
    }
    return doc[`${k}_en`] || doc[`${k}_ml`] || "";
  };

  const sourceId = `ml-${section}-${slug}-${lang}`;
  const isThis = audio?.source?.id === sourceId;
  const langName = lang === "ml" ? "Malayalam" : "English";

  const onListen = () => {
    if (!audio) return;
    if (isThis && audio.state === "loading") return;
    if (isThis && audio.state === "playing") { audio.pause(); return; }
    if (isThis && audio.state === "paused") { audio.resume(); return; }
    const text = [pick("title"), pick("summary"), toPlainText(pick("body"))].filter(Boolean).join(". ");
    audio.playText(text, langName, { id: sourceId, label: pick("title") });
  };

  const listenLabel = isThis && audio?.state === "playing" ? t("Pause")
    : isThis && (audio?.state === "loading") ? t("Loading") : t("Listen");

  return (
    <MalayalamLayout>
      <article className="mx-auto max-w-3xl px-6 py-10" data-testid="ml-article">
        <Link to={`/malayalam/${section}`} className="inline-flex items-center gap-1 text-sm" style={{ color: C.muted }}>
          <ArrowLeft className="h-4 w-4" /> {section.replace(/-/g, " ")}
        </Link>

        {status === "loading" && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: C.gold }} /></div>}

        {status === "missing" && (
          <div className="py-16 text-center" data-testid="ml-article-missing">
            <p style={{ color: C.muted }}>{t("Content from the archive will appear here as it is migrated and reviewed.")}</p>
          </div>
        )}

        {status === "ok" && doc && (
          <div className={lang === "ml" ? "ml-lang-ml" : ""}>
            <div className="mb-2 mt-4 h-1 w-14 rounded-full" style={{ background: C.gold }} />
            <h1 className="font-display text-4xl font-bold leading-tight" style={{ color: C.navy }} data-testid="ml-article-title">
              {pick("title")}
            </h1>
            <div className="mt-2 flex items-center gap-3 text-sm" style={{ color: C.muted }}>
              {doc.date && <span>{doc.date}</span>}
              {doc.author && <span>· {doc.author}</span>}
              {lang === "ml" && translating && (
                <span className="inline-flex items-center gap-1" data-testid="ml-article-translating">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: C.gold }} /> {t("Loading")}
                </span>
              )}
              <button onClick={onListen} className="ml-auto inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-medium transition-colors" style={{ borderColor: isThis ? C.gold : C.line, color: C.navy, background: isThis ? "#FBF3DE" : "transparent" }} data-testid="ml-article-listen">
                {isThis && audio?.state === "loading"
                  ? <Loader2 className="h-4 w-4 animate-spin" style={{ color: C.gold }} />
                  : isThis && audio?.state === "playing"
                    ? <Pause className="h-4 w-4" style={{ color: C.gold }} />
                    : <Headphones className="h-4 w-4" style={{ color: C.gold }} />}
                {listenLabel}
              </button>
            </div>
            {doc.cover_image && (
              <img src={fileUrl(doc.cover_image)} alt="" className="mt-5 w-full rounded-xl object-cover" style={{ maxHeight: 420 }} />
            )}
            {pick("summary") && (
              <p className="mt-5 text-lg font-medium" style={{ color: C.ink }}>{pick("summary")}</p>
            )}
            <div className="ml-prose mt-5 leading-relaxed" style={{ color: C.ink }}
              data-testid="ml-article-body" dangerouslySetInnerHTML={{ __html: pick("body") }} />
            {doc.images?.length > 0 && (
              <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="ml-article-gallery">
                {doc.images.map((u, i) => (
                  <a key={i} href={fileUrl(u)} target="_blank" rel="noreferrer" className="overflow-hidden rounded-lg border" style={{ borderColor: C.line }}>
                    <img src={fileUrl(u)} alt="" className="h-40 w-full object-cover transition-transform hover:scale-105" />
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </article>
      {status === "ok" && doc && (
        <div className={lang === "ml" ? "ml-lang-ml" : ""}>
          <Discussion entityType="ml_article" entityId={doc.id} title={t("Discussion")} />
        </div>
      )}
    </MalayalamLayout>
  );
}
